import type { Resource } from '../../../resources';

const logsPrompts = [
  'Find unusual patterns in recent logs',
  'Identify the most frequent log messages',
  'Summarize what happened in the last hour',
  'Show me errors that spiked in the last 30 minutes',
  'Find log messages related to authentication failures',
  'What services are generating the most logs?',
  'Identify slow database queries from the logs',
  'Show me logs from failed deployments',
  'Find timeout errors across all services',
  'What changed in log patterns since yesterday?',
  'Show me warning logs that might indicate problems',
  'Find logs related to memory or CPU issues',
  'Identify the noisiest log sources',
  'Show me logs with HTTP 5xx status codes',
  'Find correlation between error logs across services',
  'What are the most common error messages today?',
  'Show me logs from the last deployment window',
  'Find logs with unusual response times',
  'Identify log patterns that precede outages',
  'Show me logs related to rate limiting',
  'Find recurring error patterns in the last 24 hours',
  'What services had error spikes overnight?',
  'Show me the first occurrence of this error',
  'Find logs where latency exceeded the p99',
  'Summarize error trends across all services',
];

const tracesPrompts = [
  'Show me the slowest requests recently',
  'Find traces with unusual performance patterns',
  'Identify bottlenecks in my application flow',
  'What are the slowest API endpoints right now?',
  'Find traces where database calls took over 1 second',
  'Show me the critical path for my slowest requests',
  'Identify services with the highest error rates',
  'Find traces with excessive fan-out',
  'What external dependencies are causing latency?',
  'Show me traces that hit the timeout threshold',
  'Find performance regressions since last deploy',
  'Identify traces with retry storms',
  'Which spans contribute most to overall latency?',
  'Show me traces where caching missed',
  'Find requests that trigger the most downstream calls',
  'What is the p99 latency for my top endpoints?',
  'Identify traces with connection pool exhaustion',
  'Show me cross-service latency for this endpoint',
  'Find traces where queue wait time is excessive',
  'What services are the biggest latency contributors?',
  'Show me traces with failed health checks',
  'Find traces where payload size caused slowdowns',
  'Identify cascading failures across services',
  'Show me the trace breakdown for this operation',
  'Find traces affected by cold starts',
];

const errorsPrompts = [
  'Help me investigate this error',
  'What are the top error patterns right now?',
  'Find errors that started happening recently',
  'Show me the stack trace for the most common error',
  'Which errors are affecting the most users?',
  'Find errors that correlate with recent deployments',
  'What is the error rate trend for this service?',
  'Identify new error types that appeared today',
  'Show me errors grouped by root cause',
  'Find errors that only happen in specific environments',
  'What errors are happening on this endpoint?',
  'Identify flaky errors vs persistent failures',
  'Show me the error timeline for the last incident',
  'Find unhandled exceptions across all services',
  'Which error has the highest rate of increase?',
  'Show me errors related to third-party services',
  'Find null pointer or undefined reference errors',
  'What errors are customers reporting right now?',
  'Identify errors with missing or bad error handling',
  'Show me errors that happen during peak traffic',
  'Find errors related to configuration changes',
  'What is causing 500 errors on this route?',
  'Show me the most impactful errors by user count',
  'Find errors that only happen intermittently',
  'Identify error patterns that suggest data issues',
];

const sessionsPrompts = [
  'Find sessions with unusual behavior patterns',
  'Show me the longest user sessions today',
  'Identify sessions that ended unexpectedly',
  'What pages have the highest bounce rate?',
  'Find sessions where users encountered errors',
  'Show me sessions with rage clicks',
  'Identify the most common user journeys',
  'Find sessions where performance was degraded',
  'What features are users struggling with?',
  'Show me sessions from users who submitted support tickets',
  'Identify sessions with excessive page load times',
  'Find sessions where users abandoned checkout',
  'What is the average session duration by feature?',
  'Show me sessions with network connectivity issues',
  'Find sessions where JavaScript errors occurred',
  'Identify drop-off points in the onboarding flow',
  'Show me sessions from users on mobile devices',
  'Find sessions affected by layout shifts',
  'What user actions lead to the most errors?',
  'Show me sessions with slow API responses',
  'Find sessions where users repeated the same action',
  'Identify sessions with accessibility issues',
  'Show me sessions from high-value accounts',
  'Find sessions where users switched between features',
  'What are the most common exit pages?',
];

const contextsPrompts = [
  'Find users in California',
  'Show device contexts running iOS',
  'Contexts that signed up after January 2024',
  'Find users with a country attribute',
  'Show organization contexts named Acme',
  'Find device or organization contexts',
  'Users whose name is not Sandy',
  'Samsung devices',
  'Contexts with a name attribute',
  'Users on the enterprise plan',
];

const promptsByResource: Record<string, string[]> = {
  Logs: logsPrompts,
  Traces: tracesPrompts,
  Errors: errorsPrompts,
  Sessions: sessionsPrompts,
  Contexts: contextsPrompts,
};

/**
 * Returns the full pool of suggested prompts for a resource.
 */
export function getAllSuggestedPrompts(resource?: Resource): string[] {
  if (!resource) {
    return [];
  }
  return promptsByResource[resource] ?? [];
}

/**
 * Pick `count` random prompts from the pool for a resource,
 * avoiding duplicates from `exclude`.
 */
export function pickRandomPrompts(
  allPrompts: string[],
  count: number,
  exclude: Set<string> = new Set(),
  rand: () => number = Math.random,
): string[] {
  const available = allPrompts.filter((p) => !exclude.has(p));
  const shuffled = [...available].sort(() => rand() - 0.5);
  return shuffled.slice(0, count);
}
